import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Users, 
  Clock, 
  Award, 
  Star, 
  CheckCircle, 
  BookOpen, 
  Globe,
  Mail,
  ArrowRight,
  Target,
  Briefcase,
  TrendingUp,
  Home,
  ArrowLeft
} from "lucide-react";

interface EmbedCSPProps {
  compact?: boolean;
  showHero?: boolean;
  showQuickFacts?: boolean;
  showLearningAreas?: boolean;
  showBenefits?: boolean;
  showIdealFor?: boolean;
  showNextSteps?: boolean;
  maxWidth?: string;
}

const EmbedCSP = ({ 
  compact = false,
  showHero = true,
  showQuickFacts = true,
  showLearningAreas = true,
  showBenefits = true,
  showIdealFor = true,
  showNextSteps = true,
  maxWidth = "6xl"
}: EmbedCSPProps) => {
  const keyAreas = [
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Strategic Planning",
      description: "Executive-level decision-making and enterprise-wide security strategy development"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Cross-Functional Leadership",
      description: "Leading diverse teams across IT, OT, compliance, and physical security domains"
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Governance & Risk Management",
      description: "Advanced governance frameworks and comprehensive risk assessment methodologies"
    },
    {
      icon: <Briefcase className="w-6 h-6" />,
      title: "Stakeholder Engagement",
      description: "Executive communication and stakeholder management in complex security environments"
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: "Scenario-Based Workshops",
      description: "Practical tabletop exercises and real-world security scenario planning"
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Compliance Integration",
      description: "Advanced compliance strategies across multiple regulatory frameworks"
    }
  ];

  const benefits = [
    "Strategic insight for enterprise-wide security initiatives",
    "Operational leadership skills for cross-functional teams",
    "Executive-level decision-making capabilities",
    "Advanced governance and risk management expertise",
    "Practical experience through scenario-based learning",
    "Industry recognition for strategic security leadership"
  ];

  const idealFor = [
    "CSCE certified professionals ready for leadership roles",
    "Security managers overseeing enterprise environments",
    "IT leaders expanding into converged security strategy",
    "Risk management professionals seeking advanced skills",
    "Compliance officers managing complex security frameworks",
    "Senior professionals driving security transformation"
  ];

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 font-sans relative">
      {/* Navigation Bar - Upper Right */}
      <div className="absolute top-4 right-6 flex gap-2 z-10">
        <Button 
          variant="outline" 
          size="sm"
          className="bg-white border-gray-300 hover:bg-gray-50"
          onClick={() => window.history.back()}
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <Button 
          variant="outline" 
          size="sm"
          className="bg-white border-gray-300 hover:bg-gray-50"
          onClick={() => window.open('https://convergedsecurity.es/', '_blank')}
        >
          <Home className="w-4 h-4 mr-1" />
          Home
        </Button>
      </div>
      {/* Hero Section */}
      {showHero && (
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-12">
          <div className={`max-w-${maxWidth} mx-auto px-4`}>
            <div className={`grid ${compact ? 'lg:grid-cols-1 text-center' : 'lg:grid-cols-2'} gap-8 items-center`}>
              <div>
                <Badge className="bg-white/20 text-white border-white/30 mb-3">
                  Advanced Certification
                </Badge>
                <h1 className={`${compact ? 'text-3xl md:text-4xl' : 'text-4xl md:text-5xl'} font-bold mb-4`}>
                  CSP
                </h1>
                <h2 className={`${compact ? 'text-xl md:text-2xl' : 'text-2xl md:text-3xl'} font-semibold mb-3 text-blue-100`}>
                  Converged Security Professional
                </h2>
                <p className={`${compact ? 'text-base' : 'text-lg'} text-blue-100 mb-6 leading-relaxed`}>
                  Advanced credential for strategic insight and operational leadership in converged security.
                </p>
                {!compact && (
                  <div className="flex flex-wrap gap-3">
                    <Button 
                      size={compact ? "default" : "lg"} 
                      className="bg-white text-blue-600 hover:bg-blue-50"
                      onClick={() => window.open('mailto:info@convergedsecurity.es?subject=CSP Certification Application&body=Hello, I would like to apply for the CSP (Converged Security Professional) certification program. Please send me the application details and next steps.', '_blank')}
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Apply Now
                    </Button>
                    <Button 
                      size={compact ? "default" : "lg"} 
                      variant="outline" 
                      className="border-white text-white hover:bg-white/10"
                      onClick={() => window.open('https://pay.sumup.com/b2c/QH1JMRP5', '_blank')}
                    >
                      <Star className="w-4 h-4 mr-2" />
                      Pay Now - €750
                    </Button>
                  </div>
                )}
              </div>
              {!compact && (
                <div className="flex justify-center">
                  <div className="relative">
                    <img 
                      src="./images/csp.png" 
                      alt="CSP Certification Badge"
                      className="w-60 h-60 object-contain drop-shadow-2xl"
                    />
                    <div className="absolute -inset-4 bg-gradient-to-r from-blue-400/20 to-indigo-400/20 rounded-full blur-xl"></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quick Facts */}
      {showQuickFacts && (
        <div className={`${compact ? 'py-8' : 'py-12'} bg-white`}>
          <div className={`max-w-${maxWidth} mx-auto px-4`}>
            <div className={`grid ${compact ? 'grid-cols-2 md:grid-cols-4' : 'md:grid-cols-4'} gap-4`}>
              <Card className="text-center border-blue-200">
                <CardContent className={compact ? "pt-4" : "pt-6"}>
                  <Star className={`${compact ? 'w-6 h-6' : 'w-8 h-8'} text-blue-600 mx-auto mb-2`} />
                  <h3 className={`font-bold ${compact ? 'text-lg' : 'text-2xl'} text-gray-900`}>€750</h3>
                  <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-600`}>Per Student</p>
                </CardContent>
              </Card>
              <Card className="text-center border-blue-200">
                <CardContent className={compact ? "pt-4" : "pt-6"}>
                  <Clock className={`${compact ? 'w-6 h-6' : 'w-8 h-8'} text-blue-600 mx-auto mb-2`} />
                  <h3 className={`font-bold ${compact ? 'text-lg' : 'text-2xl'} text-gray-900`}>3 Years</h3>
                  <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-600`}>Validity Period</p>
                </CardContent>
              </Card>
              <Card className="text-center border-blue-200">
                <CardContent className={compact ? "pt-4" : "pt-6"}>
                  <Users className={`${compact ? 'w-6 h-6' : 'w-8 h-8'} text-blue-600 mx-auto mb-2`} />
                  <h3 className={`font-bold ${compact ? 'text-sm' : 'text-xl'} text-gray-900`}>CSCE Required</h3>
                  <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-600`}>Prerequisite</p>
                </CardContent>
              </Card>
              <Card className="text-center border-blue-200">
                <CardContent className={compact ? "pt-4" : "pt-6"}>
                  <Award className={`${compact ? 'w-6 h-6' : 'w-8 h-8'} text-blue-600 mx-auto mb-2`} />
                  <h3 className={`font-bold ${compact ? 'text-lg' : 'text-2xl'} text-gray-900`}>On-Site</h3>
                  <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-600`}>Small Group Format</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}

      {/* Key Learning Areas */}
      {showLearningAreas && (
        <div className={compact ? 'py-8' : 'py-12'}>
          <div className={`max-w-${maxWidth} mx-auto px-4`}>
            <div className="text-center mb-8">
              <h2 className={`${compact ? 'text-2xl md:text-3xl' : 'text-3xl md:text-4xl'} font-bold text-gray-900 mb-3`}>
                Strategic Leadership Development
              </h2>
              <p className={`${compact ? 'text-base' : 'text-lg'} text-gray-600 max-w-2xl mx-auto`}>
                Advanced curriculum focused on strategic planning and operational leadership
              </p>
            </div>
            
            <div className={`grid ${compact ? 'md:grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-3'} gap-6`}>
              {keyAreas.slice(0, compact ? 4 : 6).map((area, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow duration-300 border-blue-100">
                  <CardHeader className={compact ? "pb-2" : "pb-3"}>
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`p-2 bg-blue-100 rounded-lg text-blue-600`}>
                        {React.cloneElement(area.icon, { 
                          className: compact ? "w-4 h-4" : "w-6 h-6" 
                        })}
                      </div>
                      <CardTitle className={compact ? "text-base" : "text-lg"}>{area.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className={compact ? "pt-0" : ""}>
                    <p className={`${compact ? 'text-sm' : 'text-base'} text-gray-600`}>{area.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Program Benefits */}
      {showBenefits && (
        <div className={`${compact ? 'py-8' : 'py-12'} bg-gradient-to-r from-blue-600 to-indigo-700 text-white`}>
          <div className={`max-w-${maxWidth} mx-auto px-4`}>
            <div className={`grid ${compact ? 'lg:grid-cols-1' : 'lg:grid-cols-2'} gap-8 items-center`}>
              <div>
                <h2 className={`${compact ? 'text-2xl md:text-3xl' : 'text-3xl md:text-4xl'} font-bold mb-4`}>
                  Why Choose CSP?
                </h2>
                <p className={`${compact ? 'text-base' : 'text-lg'} text-blue-100 mb-6`}>
                  CSP elevates CSCE professionals to strategic leadership roles.
                </p>
                <div className="space-y-3">
                  {benefits.slice(0, compact ? 4 : 6).map((benefit, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} text-blue-200 flex-shrink-0 mt-0.5`} />
                      <p className={`${compact ? 'text-sm' : 'text-base'} text-blue-100`}>{benefit}</p>
                    </div>
                  ))}
                </div>
              </div>
              {!compact && (
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                  <h3 className="text-xl font-bold mb-4">Program Highlights</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Users className="w-4 h-4 text-blue-200" />
                      <span className="text-sm">Small group format</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Target className="w-4 h-4 text-blue-200" />
                      <span className="text-sm">Scenario-based workshops</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Briefcase className="w-4 h-4 text-blue-200" />
                      <span className="text-sm">Group presentation assessment</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Globe className="w-4 h-4 text-blue-200" />
                      <span className="text-sm">Digital certificate and badge</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Ideal Candidates */}
      {showIdealFor && (
        <div className={`${compact ? 'py-8' : 'py-12'} bg-white`}>
          <div className={`max-w-${maxWidth} mx-auto px-4`}>
            <div className="text-center mb-8">
              <h2 className={`${compact ? 'text-2xl md:text-3xl' : 'text-3xl md:text-4xl'} font-bold text-gray-900 mb-3`}>
                Perfect For
              </h2>
              <p className={`${compact ? 'text-base' : 'text-lg'} text-gray-600 max-w-2xl mx-auto`}>
                CSP is designed for CSCE-certified professionals ready for leadership
              </p>
            </div>
            
            <div className={`grid ${compact ? 'md:grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-3'} gap-4`}>
              {idealFor.slice(0, compact ? 4 : 6).map((candidate, index) => (
                <Card key={index} className="border-blue-100 hover:border-blue-300 transition-colors">
                  <CardContent className={compact ? "pt-4" : "pt-6"}>
                    <div className="flex items-start gap-3">
                      <ArrowRight className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} text-blue-600 flex-shrink-0 mt-0.5`} />
                      <p className={`${compact ? 'text-sm' : 'text-base'} text-gray-700`}>{candidate}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Next Steps */}
      {showNextSteps && (
        <div className={`${compact ? 'py-8' : 'py-12'} bg-gradient-to-br from-blue-50 to-indigo-50`}>
          <div className={`max-w-${compact ? '3xl' : '4xl'} mx-auto px-4 text-center`}>
            <h2 className={`${compact ? 'text-2xl md:text-3xl' : 'text-3xl md:text-4xl'} font-bold text-gray-900 mb-4`}>
              Ready to Lead?
            </h2>
            <p className={`${compact ? 'text-base' : 'text-lg'} text-gray-600 mb-6`}>
              Take your CSCE certification to the next level with strategic leadership training
            </p>
            
            <Card className="border-blue-200 shadow-lg">
              <CardContent className={compact ? "pt-6" : "pt-8"}>
                <div className={`grid ${compact ? 'grid-cols-3' : 'md:grid-cols-3'} gap-4 mb-6`}>
                  <div className="text-center">
                    <div className={`${compact ? 'w-8 h-8' : 'w-12 h-12'} bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2`}>
                      <span className={`text-blue-600 font-bold ${compact ? 'text-sm' : ''}`}>1</span>
                    </div>
                    <h3 className={`font-semibold mb-1 ${compact ? 'text-sm' : ''}`}>Verify CSCE</h3>
                    <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-600`}>Ensure CSCE certification is current</p>
                  </div>
                  <div className="text-center">
                    <div className={`${compact ? 'w-8 h-8' : 'w-12 h-12'} bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2`}>
                      <span className={`text-blue-600 font-bold ${compact ? 'text-sm' : ''}`}>2</span>
                    </div>
                    <h3 className={`font-semibold mb-1 ${compact ? 'text-sm' : ''}`}>Apply & Attend</h3>
                    <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-600`}>Join on-site strategic training</p>
                  </div>
                  <div className="text-center">
                    <div className={`${compact ? 'w-8 h-8' : 'w-12 h-12'} bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2`}>
                      <span className={`text-blue-600 font-bold ${compact ? 'text-sm' : ''}`}>3</span>
                    </div>
                    <h3 className={`font-semibold mb-1 ${compact ? 'text-sm' : ''}`}>Lead & Certified</h3>
                    <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-600`}>Complete and receive CSP</p>
                  </div>
                </div>
                
                <div className="flex flex-wrap justify-center gap-3">
                  <Button 
                    size={compact ? "default" : "lg"} 
                    className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                    onClick={() => window.open('https://pay.sumup.com/b2c/QH1JMRP5', '_blank')}
                  >
                    <Star className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} mr-2`} />
                    Pay Now - €750
                  </Button>
                  <Button 
                    size={compact ? "default" : "lg"} 
                    variant="outline" 
                    className="border-blue-300 text-blue-600 hover:bg-blue-50"
                    onClick={() => window.open('mailto:info@convergedsecurity.es?subject=CSP Certification Application&body=Hello, I would like to apply for the CSP (Converged Security Professional) certification program. I am CSCE certified and ready for strategic leadership training. Please send me the application details and next steps.', '_blank')}
                  >
                    <Mail className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} mr-2`} />
                    Contact Us
                  </Button>
                  {!compact && (
                    <Button 
                      size="lg" 
                      variant="outline" 
                      className="border-blue-300 text-blue-600 hover:bg-blue-50"
                      onClick={() => window.open('mailto:info@convergedsecurity.es?subject=CSP Brochure Request&body=Hello, I would like to download the CSP (Converged Security Professional) certification brochure. Please send me the detailed program information.', '_blank')}
                    >
                      <BookOpen className="w-5 h-5 mr-2" />
                      Download Brochure
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmbedCSP;